/**
 * Normalizes a string for locale-insensitive comparison by:
 * - Trimming surrounding whitespace
 * - Converting to lower-case
 * - Decomposing accented characters (NFD) and stripping the accent code points
 *   so that e.g. "é" matches "e"
 * - Replacing en-dashes (–) and em-dashes (—) with a regular hyphen (-)
 *
 * @param text - The string to normalize.
 * @returns The normalized string.
 */
export const normalizeString = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .normalize("NFD") // é (U+00E9) → e (U+0065) + ◌́ (U+0301)  — splits char + accent into two code points
    .replace(/[\u0300-\u036f]/g, "") // drop the detached accent code points:  e + ◌́ → e
    .replace(/[\u2013\u2014]/g, "-"); // – (en-dash) / — (em-dash) → - (hyphen)

/**
 * Returns `true` when `target` contains `searchValue` after both strings have
 * been passed through {@link normalizeString}.
 *
 * This makes the check case-insensitive, accent-insensitive, and
 * dash-insensitive, which is suitable for powering search/filter UIs.
 *
 * @param searchValue - The term the user typed.
 * @param target - The string to search within (e.g. an item label).
 * @returns `true` if the normalized target includes the normalized search value.
 *
 * @example
 * searchWithNormalizedInclude("cafe", "Café Latte") // true
 * searchWithNormalizedInclude("re–use", "re-use pattern") // true
 */
export const searchWithNormalizedInclude = (
  searchValue: string,
  target: string
) => normalizeString(target).includes(normalizeString(searchValue));
